import { Link } from '@/lib/react-router-dom-shim';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLocale } from '@/contexts/LocaleContext';
import { MapPin } from 'lucide-react';

interface FeaturedDestination {
  slug: string;
  name: string;
  state: string | null;
  hero_image_url: string | null;
  slogan_es: string | null;
  slogan_en: string | null;
  slogan_pt: string | null;
}

export const HomeFeaturedDestinations = ({ limit = 6 }: { limit?: number }) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const { data: destinations = [], isLoading } = useQuery({
    queryKey: ['home-featured-destinations', limit],
    queryFn: async (): Promise<FeaturedDestination[]> => {
      const { data, error } = await supabase
        .from('destinations')
        .select('slug, name, state, hero_image_url, slogan_es, slogan_en, slogan_pt, visit_count')
        .eq('status', 'published')
        .order('visit_count', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as FeaturedDestination[];
    },
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  if (isLoading || destinations.length === 0) return null;

  const pickSlogan = (d: FeaturedDestination) =>
    (locale === 'pt-BR' ? d.slogan_pt : locale === 'es' ? d.slogan_es : d.slogan_en) || '';

  return (
    <section className="py-12 md:py-16 content-visibility-auto" aria-labelledby="featured-destinations-heading">
      <div className="container mx-auto px-4">
        <div className="mb-10 text-center">
          <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
            {t('home.featuredDestinations.subtitle') || 'Guias de viagem'}
          </p>
          <h2
            id="featured-destinations-heading"
            className="font-serif text-3xl font-bold text-foreground md:text-4xl"
          >
            {t('home.featuredDestinations.title') || 'Destinos em destaque'}
          </h2>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {destinations.map((d) => (
            <Link
              key={d.slug}
              to={`/destino/${d.slug}`}
              className="group relative block overflow-hidden rounded-lg border bg-card shadow-sm transition-all hover:shadow-lg"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                {d.hero_image_url ? (
                  <img
                    src={d.hero_image_url}
                    alt={d.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-primary/10">
                    <MapPin className="h-10 w-10 text-primary/50" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-4 text-white">
                  <h3 className="font-serif text-xl font-bold">{d.name}</h3>
                  {d.state && (
                    <p className="text-sm text-white/80 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {d.state}
                    </p>
                  )}
                </div>
              </div>
              {pickSlogan(d) && (
                <div className="p-4">
                  <p className="text-sm text-muted-foreground line-clamp-2">{pickSlogan(d)}</p>
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
